import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import CountersSection from './components/CountersSection';
import ContactForm from './components/ContactForm';

const App = () => {
  const epsLogos = [
    'https://upload.wikimedia.org/wikipedia/commons/1/12/Logo_nueva_eps.png',
    'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0f/EPS_Sanitas_Colombia_logo.svg/1280px-EPS_Sanitas_Colombia_logo.svg.png',
    'https://www.uam.edu.co/wp-content/uploads/2022/09/Logo-Seguros-SURA.jpg',
    'https://staticnew-prod.topdoctors.com.co/insurance/100180/image/medium/572ccd6a-4c2c-4245-8729-7d769e4504ad.png',
    'https://saludtotal.com.co/wp-content/uploads/2020/10/logo-st.png',
    'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0a/Compensar_logo.svg/2560px-Compensar_logo.svg.png'
  ];

  const plans = [
    {
      title: "SOLO EPS",
      description: "Ideal para asegurar tu salud y la de tu familia con lo esencial.",
      features: [
        "Afiliación a EPS",
        "Cobertura de salud",
        "Asesoría inicial",
        "Soporte vía WhatsApp"
      ],
      firstPayment: "$100.000 COP",
      monthlyPayment: "$140.000 COP",
      buttonText: "Quiero afiliarme",
      whatsappLink: "https://wa.me/573153283570",
      colorClass: "from-blue-500 to-blue-600"
    },
    {
      title: "EPS + PENSION",
      description: "Protege tu salud hoy y asegura tu futuro con una pensión digna.",
      features: [
        "Afiliación a EPS y Fondo de Pensión",
        "Cobertura de salud completa",
        "Asegura tu jubilación",
        "Asesoría personalizada"
      ],
      firstPayment: "$370.000 COP",
      monthlyPayment: "$415.000 COP",
      buttonText: "Quiero afiliarme",
      whatsappLink: "https://wa.me/573153283570",
      colorClass: "from-purple-500 to-purple-600"
    },
    {
      title: "EPS + CAJA + PENSION",
      description: "La solución más completa para tu bienestar y el de tu hogar.",
      features: [
        "Afiliación a EPS, Pensión y Caja de Compensación",
        "Acceso a subsidios y beneficios",
        "Recreación y educación"
      ],
      firstPayment: "$440.000 COP",
      monthlyPayment: "$486.000 COP",
      buttonText: "Quiero afiliarme",
      whatsappLink: "https://wa.me/573153283570",
      colorClass: "from-green-500 to-green-600"
    }
  ];

  const getRandomImage = () => `https://picsum.photos/1024/768?random=${Math.random()}`;

  return (
    <>
      {/* SEO y Metadatos */}
      <Helmet>
        <title>Asesoría Seguridad Social – Planes desde $100.000 COP</title>
        <meta
          name="description"
          content="Afíliate a tu EPS favorita. Planes de salud, pensión y caja de compensación. ¡Consulta gratis ahora!"
        />
        <meta
          name="keywords"
          content="afiliación EPS, afiliación Nueva EPS, afiliación Sanitas, afiliación Sura, afiliación Famisanar, afiliación Salud Total, afiliación Compensar, seguridad social en Colombia, pensión"
        />
        <link rel="canonical" href="https://asesoriaseguridadsocial.com.co/" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Asesoría Seguridad Social – Planes desde $100.000 COP"
        />
        <meta
          property="og:description"
          content="Augura tu tranquilidad: afiliación a EPS, pensión y caja de compensación."
        />
        <meta property="og:url" content="https://asesoriaseguridadsocial.com.co/" />
        <meta
          property="og:image"
          content="https://asesoriaseguridadsocial.com.co/og-image.jpg"
        />

        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@EnithgmaSAS" />
        <meta
          name="twitter:title"
          content="Asesoría Seguridad Social – Planes desde $100.000 COP"
        />
        <meta
          name="twitter:description"
          content="Afíliate a tu EPS con un clic: planes accesibles y asesoría experta."
        />
        <meta
          name="twitter:image"
          content="https://asesoriaseguridadsocial.com.co/twitter-card.jpg"
        />

        {/* JSON-LD Schema.org */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Asesoría en Seguridad Social",
            "provider": { "@type": "Organization", "name": "Enithgma SAS" },
            "areaServed": "CO",
            "serviceType": "Afiliación EPS",
            "url": "https://asesoriaseguridadsocial.com.co",
            "telephone": "+573153283570",
            "email": "consultorias.legal@gmail.com"
          }
        `}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

        {/* Elfsight WhatsApp Widget */}
        <div
          className="elfsight-app-8434c312-3ec4-4f9d-8e7f-e615010424c7"
          data-elfsight-app-lazy
        ></div>

        {/* Hero Section */}
        <section
          className="relative bg-cover bg-center py-20 h-[768px] md:py-32 text-white flex items-center justify-center"
          style={{ backgroundImage: `url(/familia.png)` }}
        >
          <div className="absolute inset-0 bg-black opacity-60"></div>
          <div className="relative z-10 text-center px-4">
            <motion.h2
              className="text-4xl md:text-5xl font-extrabold leading-tight mb-4 drop-shadow-lg"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              ¡Tu Seguridad Social al Alcance de tu Mano!
            </motion.h2>
            <motion.p
              className="text-2xl md:text-3xl font-bold mb-8 drop-shadow-lg"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Afíliate hoy a Solo EPS por solo{' '}
              <span className="text-green-400">$100.000 COP</span>
            </motion.p>
            <motion.a
              href="#planes-asesoria"
              className="bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 font-bold py-3 px-8 rounded-full shadow-lg flex items-center justify-center mx-auto w-fit transition duration-300 ease-in-out transform hover:scale-105"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById('planes-asesoria')
                  .scrollIntoView({ behavior: 'smooth' });
              }}
            >
              ¡Conoce Nuestros Planes Ahora!
            </motion.a>
          </div>
        </section>

        <HeroSection />

          {/* Plans Section */}
        <section
          id="planes-asesoria"
          className="py-16 bg-gradient-to-br from-gray-50 to-gray-100"
        >
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-12">
              Nuestros Planes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-xl shadow-2xl border border-gray-200 p-8 flex flex-col items-center text-center transform transition-all duration-300 hover:scale-105 hover:shadow-3xl"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: index * 0.2 + 0.2 }}
                >
                  <div
                    className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 bg-gradient-to-br ${plan.colorClass} text-white text-5xl font-extrabold shadow-lg`}
                  >
                    {index + 1}
                  </div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-4">
                    {plan.title}
                  </h3>
                  <p className="text-gray-600 mb-6 flex-grow">
                    {plan.description}
                  </p>
                  <ul className="text-left text-gray-700 mb-8 space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center">
                        <svg
                          className="w-6 h-6 text-green-500 mr-3 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-lg">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mb-6">
                    <p className="text-l font-semibold text-gray-700">
                      Primer Mes:
                    </p>
                    <p className="text-3xl font-extrabold text-gray-900">
                      {plan.firstPayment}
                    </p>
                    <p className="text-lg font-semibold text-gray-700 mt-2">
                      A partir del segundo mes:
                    </p>
                    <p className="text-4xl font-extrabold text-gray-900">
                      {plan.monthlyPayment}
                    </p>
                  </div>
                  <motion.a
                    href={plan.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full py-3 px-8 rounded-full font-bold text-white shadow-lg transition duration-300 ease-in-out transform hover:scale-105 bg-gradient-to-r ${plan.colorClass}`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {plan.buttonText}
                  </motion.a>
                </motion.div>
              ))}
            </div>
          </div>
        </section>


         {/* EPS Logos Section */}
        <section className="py-16 bg-white shadow-inner">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-10">
              Trabajamos con las principales EPS de Colombia
            </h2>
            <motion.div
              className="flex flex-wrap justify-center items-center gap-8 md:gap-12"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
              }}
            >
              {epsLogos.map((logo, index) => (
                <motion.div
                  key={index}
                  className="p-4 bg-white rounded-lg flex items-center justify-center transition-shadow duration-300"
                  variants={{ hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1 } }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img
                    src={logo}
                    alt={`Logo EPS ${index + 1}`}
                    className="h-16 md:h-20 object-contain"
                    loading="lazy"
                  />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
        
        <CountersSection />

        <ContactForm />

        <footer className="bg-gray-800 text-white py-8">
          <div className="container mx-auto px-4 text-center">
            <p className="text-lg mb-2">
              © {new Date().getFullYear()} Asesoría Seguridad Social. Todos los derechos reservados.
            </p>
            <p className="text-md">
              Hecho con <span role="img" aria-label="corazón">❤️</span> por Enithgma SAS - <a href="https://enithgma.com" className="underline">enithgma.com</a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default App;
